import React from "react";
import * as DOMPurify from "dompurify";
import { Modal } from "./modal";
import AudioPlayer from "./audio-player";
import { Icon } from "./icon";
import {
  AlertType,
  commMethodMeta,
  getCommMethodFromConfig,
} from "../utilities/constants";

function getValue(template, key) {
  return template?.[key] ?? template?.[key[0].toUpperCase() + key.slice(1)];
}

export function TemplatePreviewModal({ template, loading, error, onClose }) {
  const templateName = getValue(template, "subject") || "Template Preview";
  const createdBy = getValue(template, "createdBy") || "Message Sender";
  const avatarUrl = getValue(template, "avatarUrl");
  const alertTypeId = getValue(template, "alertTypeId");
  const templateType = getValue(template, "templateType");
  const content = getValue(template, "template") || "";
  const voiceFileName = getValue(template, "voiceFileName");
  const commMethod = getCommMethodFromConfig(alertTypeId, templateType);
  const methodMeta = commMethodMeta(commMethod) || {
    label: "Text",
    iconClass: "fa fa-mobile",
  };
  const hasAudio = !!content;
  const sanitizedContent = DOMPurify.sanitize(content || "");

  return (
    <Modal className="template-preview-modal" onClose={onClose}>
      <div className="template-preview-modal-content">
        <Icon icon="times" className="close" onClick={onClose} />

        <div className="template-preview-modal-header">
          <div className="template-preview-modal-title-wrap">
            <i className="fa fa-eye" aria-hidden="true" />
            <h3 className="template-preview-modal-title">
              <span className="template-preview-modal-title-strong">Preview:</span>{" "}
              <span>{templateName}</span>
            </h3>
          </div>
        </div>

        <div className="template-preview-modal-method-chip">
          <i className={methodMeta.iconClass} aria-hidden="true" />
          <span>{methodMeta.label}</span>
        </div>

        <div className="template-preview-modal-body">
          {loading ? (
            <div className="template-preview-placeholder">Loading preview...</div>
          ) : error ? (
            <div className="template-preview-error">{error}</div>
          ) : commMethod === AlertType.Email ? (
            <div className="template-email-preview">
              <div className="template-email-preview-subject">{templateName}</div>
              <div className="template-email-preview-sender">
                <div className="template-email-preview-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={createdBy} />
                  ) : (
                    <i className="fa fa-user" />
                  )}
                </div>
                <div className="template-email-preview-sender-info">
                  <span className="template-email-preview-sender-name">
                    {createdBy}
                  </span>
                  <span className="template-email-preview-recipient">
                    to: [recipient list]
                  </span>
                </div>
              </div>
              <div
                className="template-email-preview-body"
                dangerouslySetInnerHTML={{
                  __html: sanitizedContent,
                }}
              />
            </div>
          ) : commMethod === AlertType.Voice || commMethod === AlertType.VoiceIVR ? (
            <div className="template-preview-text-card template-preview-voice-card">
              <div className="template-preview-text-card-header">
                <div className="template-preview-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={createdBy} />
                  ) : (
                    <i className="fa fa-user" aria-hidden="true" />
                  )}
                </div>
                <div className="template-preview-author">{createdBy}</div>
              </div>
              <div className="template-preview-text-card-body template-preview-voice-card-body">
                <p className="template-preview-voice-subject">
                  {voiceFileName || templateName}
                </p>
                {hasAudio ? (
                  <AudioPlayer src={content} styled />
                ) : (
                  <div className="template-preview-error">
                    Audio preview unavailable for this template.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="template-preview-text-card">
              <div className="template-preview-text-card-header">
                <div className="template-preview-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={createdBy} />
                  ) : (
                    <i className="fa fa-user" aria-hidden="true" />
                  )}
                </div>
                <div className="template-preview-author">{createdBy}</div>
              </div>
              <div className="template-preview-text-card-body">
                {content || (
                  <span className="template-preview-placeholder">
                    No preview content available.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
